import { useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, links, resolveFormattingLocale, useViewLevel } from '@eis/core';
import {
  ExportButton,
  EntityHeader,
  ErnLine,
  ErrorState,
  PageLayout,
  RequirePermission,
  usePermissions,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { buildGroupPolicyExportLabels } from '../model/export-labels';
import {
  useActivateGroupPolicy,
  useGroupPolicy,
  useGroupPolicyMemberSchema,
} from '../api/group-policies';
import { groupPolicyPolicyholderLabel, groupPolicyStatusOf } from '../model/types';
import { ConflictNotice } from '../../policies/ui/ConflictNotice';
import { GroupPolicyOverviewTab } from './GroupPolicyOverviewTab';
import { GroupPolicyPoliciesTab } from './GroupPolicyPoliciesTab';
import { GroupPolicyInvoicesTab } from './GroupPolicyInvoicesTab';
import { GroupPolicyClaimsTab } from './GroupPolicyClaimsTab';
import { GroupPolicyTimelineTab } from './GroupPolicyTimelineTab';
import { GroupPolicyEditDialog } from './GroupPolicyEditDialog';
import { UpgradeDialog } from '../../policies/ui/UpgradeDialog';
import { TerminationDialog } from './TerminationDialog';
import { GroupPolicyLeadsTab } from '../../leads/ui/GroupPolicyLeadsTab';

export type GroupPolicyTab = 'overview' | 'policies' | 'invoices' | 'timeline' | 'leads' | 'claims';

/**
 * Gruppen-Police detail. The active tab lives in the URL (`?tab=`) so a link to the Policen
 * tab is a link to the Policen tab — deep-linkable, bookmark-safe, audit-log-friendly (backbone §3).
 *
 * Every mutation on this screen quotes the ETag of the GET that produced what the operator is
 * looking at. The SDK captures it and replays it as `If-Match`; a 412 or a 409 renders the standard
 * "Daten wurden geändert" notice and NEVER a silent retry, because a retry is a machine deciding
 * to overwrite the other operator (decision T-12, §B.10).
 *
 * The policyholder is shown by NAME, read from the parties `GetGroupPolicy` resolves through the
 * partner directory (SDD-011 P-5; the Partner service is real since SDD-014) — and by id, marked,
 * when nobody could name it (SSOT §4.2). Never a blank.
 */
export function GroupPolicyDetail({
  groupPolicyId,
  tab,
  onTabChange,
}: {
  readonly groupPolicyId: string;
  readonly tab: GroupPolicyTab;
  readonly onTabChange: (tab: GroupPolicyTab) => void;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { isAdvanced } = useViewLevel();
  const query = useGroupPolicy(groupPolicyId);
  // What a member enrolled here is validated against, through the agreement's pin (SDD-011 P-8).
  const memberSchema = useGroupPolicyMemberSchema(groupPolicyId);
  const activate = useActivateGroupPolicy();
  const [terminating, setTerminating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  if (query.isError) {
    return (
      <PageLayout>
        <ErrorState
          description={t('groupPolicies.error.detail')}
          details={query.error.message}
          onRetry={() => void query.refetch()}
        />
      </PageLayout>
    );
  }

  const groupPolicy = query.data?.groupPolicy;
  const snapshot = query.data?.snapshot;
  // The GROUP version's roles, through the agreement's pin (SDD-011 P-8).
  const partnerRoles = query.data?.partnerRoles;

  if (groupPolicy === undefined) {
    return (
      <PageLayout header={<Skeleton variant="text" width={360} height={44} />}>
        <Skeleton variant="rounded" height={220} />
      </PageLayout>
    );
  }

  const status = groupPolicyStatusOf(groupPolicy);
  const policyholder = groupPolicyPolicyholderLabel(groupPolicy);
  const code = groupPolicy.code ?? groupPolicyId;
  const canSeePolicies = can(p.policies.view);
  // The invoices tab reads the BILLING service (SDD-015 §7.2) and is gated by its `invoices.view`
  // (a typed reference, PERMISSIONS.md): the product persona holds `group-policies.*` and sees no
  // invoices, support holds `invoices.view` and does.
  const canSeeInvoices = can(p.invoices.view);
  // The Leads tab (SDD-013 R-2) is a SECTION of this route, gated by `leads.view` like the Policen tab.
  const canSeeLeads = can(p.leads.view);
  // The Schäden tab (SDD-018 §9, prompt 26 phase 26.7): claimservice's data, gated by the claims
  // domain's own `claims.view` (a typed reference, PERMISSIONS.md).
  const canSeeClaims = can(p.claims.view);
  // A tab the persona cannot see must not stay selected via the URL.
  const activeTab: GroupPolicyTab =
    (tab === 'policies' && !canSeePolicies) ||
    (tab === 'invoices' && !canSeeInvoices) ||
    (tab === 'leads' && !canSeeLeads) ||
    (tab === 'claims' && !canSeeClaims)
      ? 'overview'
      : tab;

  return (
    <PageLayout
      header={
        <EntityHeader
          title={code}
          icon={<LayersOutlinedIcon />}
          backTo={{
            label: t('groupPolicies.back'),
            href: links.insurance.groupPolicies(),
            onNavigate: (event) => {
              event.preventDefault();
              void navigate({ href: links.insurance.groupPolicies() });
            },
          }}
          status={{ value: status, label: t(`groupPolicies.status.${status}` as never) }}
          meta={[
            {
              label: t('groupPolicies.policies.columns.policyholder'),
              // The policyholder by name, opening the partner for whoever may see Partner (SDD-014).
              value:
                (groupPolicy.policyholderPartnerId ?? '') === '' ? (
                  policyholder.text
                ) : (
                  <RequirePermission anyOf={[p.partners.view]} fallback={policyholder.text}>
                    <Link
                      href={links.partners.partner(groupPolicy.policyholderPartnerId ?? '')}
                      onClick={(event) => {
                        event.preventDefault();
                        void navigate({
                          href: links.partners.partner(groupPolicy.policyholderPartnerId ?? ''),
                        });
                      }}
                    >
                      {policyholder.text}
                    </Link>
                  </RequirePermission>
                ),
            },
            {
              label: t('groupPolicies.columns.validFrom'),
              value:
                snapshot?.validFrom === undefined || snapshot.validFrom === ''
                  ? '—'
                  : formatCivilDate(snapshot.validFrom, locale),
            },
            {
              // "Stand", not "Version": the belief being read, in the operations persona's own
              // words (SSOT I-6).
              label: t('groupPolicies.columns.recordedAt'),
              value: snapshot?.recordedAt ?? '—',
            },
            {
              // The copyable identity (T-23), beside the code a human quotes.
              label: t('groupPolicies.ern'),
              value: <ErnLine ern={groupPolicy.ern} copyLabel={t('groupPolicies.ernCopy')} />,
            },
          ]}
          chips={
            <>
              {/*
                The bound group tarif is configuration: on the operations surface it appears only
                at ViewLevel `advanced`, as the overview's binding does (SSOT I-6).
              */}
              {isAdvanced && (groupPolicy.configuration?.groupTarifVersionErn ?? '') !== '' && (
                <Chip
                  variant="outlined"
                  label={`${t('groupPolicies.overview.tariffBinding')}: ${groupPolicy.configuration?.groupTarifVersionErn ?? ''}`}
                />
              )}
              <Chip variant="outlined" color="primary" label={t('groupPolicies.singular')} />
            </>
          }
          actions={
            <>
              {/* Overview, members (with totals) and leads in one workbook (SDD-020 §8.3). */}
              <ExportButton
                service="insurance"
                permission={p.exports.create}
                buildDetailRequest={() => ({
                  resource: groupPolicy.ern ?? `insuranceservice:group_policy/${code}`,
                  labels: buildGroupPolicyExportLabels(t),
                  label: t('groupPolicies.exportLabel', { code }),
                })}
              />
              <RequirePermission anyOf={[p.groupPolicies.update]}>
                <Button
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  disabled={status === 'TERMINATED'}
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  {t('groupPolicies.edit')}
                </Button>
              </RequirePermission>
              {status === 'IN_NEGOTIATION' && (
                <RequirePermission anyOf={[p.groupPolicies.update]}>
                  <Button
                    variant="contained"
                    disabled={activate.isPending}
                    onClick={() => {
                      activate.mutate({
                        code,
                        // Activation is DATED and cannot precede the segment being activated:
                        // an agreement activated on its own start date was never in negotiation.
                        effectiveFrom: new Date().toISOString().slice(0, 10),
                      });
                    }}
                  >
                    {t('groupPolicies.activate')}
                  </Button>
                </RequirePermission>
              )}
            </>
          }
          // The upgrade and the termination behind "Weitere Aktionen" (design v2). Offered only to
          // a persona granted one of them, so the menu is never empty.
          moreActions={
            can(p.groupPolicies.update) || can(p.groupPolicies.terminate) ? (
              <>
                {/* An agreement upgrade is an endorsement of the AGREEMENT (SDD-016 §5): update. */}
                <RequirePermission anyOf={[p.groupPolicies.update]}>
                  <MenuItem
                    disabled={status === 'TERMINATED'}
                    onClick={() => {
                      setUpgrading(true);
                    }}
                  >
                    {t('policies.upgrade.action')}
                  </MenuItem>
                </RequirePermission>
                <RequirePermission anyOf={[p.groupPolicies.terminate]}>
                  <MenuItem
                    disabled={status === 'TERMINATED'}
                    onClick={() => {
                      setTerminating(true);
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    {t('groupPolicies.terminate')}
                  </MenuItem>
                </RequirePermission>
              </>
            ) : undefined
          }
        />
      }
      subHeader={
        <Tabs
          value={activeTab}
          onChange={(_event, next: GroupPolicyTab) => {
            onTabChange(next);
          }}
          aria-label={t('groupPolicies.singular')}
          sx={{ width: '100%' }}
        >
          <Tab value="overview" label={t('groupPolicies.tabs.overview')} />
          {canSeePolicies && <Tab value="policies" label={t('groupPolicies.tabs.policies')} />}
          {canSeeInvoices && <Tab value="invoices" label={t('groupPolicies.tabs.invoices')} />}
          {canSeeLeads && <Tab value="leads" label={t('groupPolicies.tabs.leads')} />}
          {canSeeClaims && <Tab value="claims" label={t('groupPolicies.tabs.claims')} />}
          <Tab value="timeline" label={t('groupPolicies.tabs.timeline')} />
        </Tabs>
      }
    >
      <Stack spacing={2.5}>
        <ConflictNotice
          conflict={activate.conflict}
          onReload={() => {
            void query.refetch();
          }}
        />

        {activeTab === 'overview' && (
          <GroupPolicyOverviewTab
            groupPolicy={groupPolicy}
            snapshot={snapshot}
            partnerRoles={partnerRoles}
            locale={locale}
          />
        )}
        {activeTab === 'policies' && canSeePolicies && (
          <GroupPolicyPoliciesTab
            code={code}
            policyholderLabel={policyholder.text}
            allowedTarifs={groupPolicy.allowedTarifs ?? []}
            memberSchema={memberSchema}
            locale={locale}
          />
        )}
        {activeTab === 'invoices' && canSeeInvoices && <GroupPolicyInvoicesTab code={code} />}
        {activeTab === 'leads' && canSeeLeads && (
          <GroupPolicyLeadsTab code={code} status={status} />
        )}
        {activeTab === 'claims' && canSeeClaims && (
          <GroupPolicyClaimsTab code={code} locale={locale} />
        )}
        {activeTab === 'timeline' && (
          <GroupPolicyTimelineTab code={code} rules={partnerRoles} locale={locale} />
        )}
      </Stack>

      <RequirePermission anyOf={[p.groupPolicies.update]}>
        <GroupPolicyEditDialog
          code={code}
          groupPolicy={groupPolicy}
          partnerRoles={partnerRoles}
          open={editing}
          onClose={() => {
            setEditing(false);
          }}
        />
      </RequirePermission>

      <RequirePermission anyOf={[p.groupPolicies.update]}>
        <UpgradeDialog
          kind="group"
          code={code}
          currentVersionErn={groupPolicy.configuration?.groupTarifVersionErn ?? ''}
          open={upgrading}
          ready={!query.isFetching}
          onClose={() => {
            setUpgrading(false);
          }}
        />
      </RequirePermission>

      <RequirePermission anyOf={[p.groupPolicies.terminate]}>
        <TerminationDialog
          code={code}
          open={terminating}
          onClose={() => {
            setTerminating(false);
          }}
        />
      </RequirePermission>
    </PageLayout>
  );
}
